/**
 * useMint Hook
 *
 * Shared hook for mint state management, used by CollectionDetailPage.
 * Handles quantity selection, payment flow, status polling, and pending mint management.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import {
  requestMint,
  getMintStatus,
  getMyMints,
  resumeMint,
  cancelMint,
  type MintStatus,
  type UserMint,
  type Eligibility,
} from '../api/drops';
import type { SigningPayload } from '../types/signing';
import { toSigningPayload } from '../lib/wallet';
import { getMintReferralCode } from '../utils/mint-referral';

export interface UseMintOptions {
  /** Launch slug for API calls */
  launchSlug: string;
  /** Launch ID for mint referral cookie lookup */
  launchId?: string;
  /** Called when mint completes successfully */
  onMintSuccess?: () => void;
  /** Called when data should be refreshed */
  onRefresh?: () => void;
}

export interface UseMintReturn {
  // Quantity state
  quantity: number;
  setQuantity: (qty: number) => void;

  // Edition drops: the buyer-selected artwork (required to mint on edition launches)
  selectedEditionId: string | null;
  setSelectedEditionId: (editionId: string | null) => void;

  // Loading/error state
  mintLoading: boolean;
  mintError: string | null;

  // Payment modal state
  showPaymentModal: boolean;
  paymentPayload: SigningPayload | null;

  // Mint status tracking
  mintStatus: MintStatus | null;
  showMintStatus: boolean;

  // Pending mint state
  pendingMint: UserMint | null;
  pendingMintCount: number;
  resumeLoading: boolean;

  // Cancel modal state
  showCancelModal: boolean;
  cancelCountdown: number;
  cancelLoading: boolean;

  // Current mint tracking
  currentMintIds: string[];
  currentBatchId: string | null;

  // Actions
  handleMint: () => Promise<void>;
  handleResume: () => Promise<void>;
  handleCancel: () => Promise<void>;
  openCancelModal: () => void;
  closeCancelModal: () => void;
  handlePaymentSuccess: (txid: string) => void;
  handlePaymentRejected: () => void;
  handlePaymentExpired: () => void;
  closeMintStatusModal: () => void;
  closePaymentModal: () => void;

  // Utilities
  checkPendingMints: () => Promise<void>;
}

export function useMint({
  launchSlug,
  launchId,
  onMintSuccess,
  onRefresh,
}: UseMintOptions): UseMintReturn {
  const { isAuthenticated, user } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();

  // Quantity state
  const [quantity, setQuantity] = useState(1);

  // Edition drops: selected artwork
  const [selectedEditionId, setSelectedEditionId] = useState<string | null>(null);

  // Loading/error state
  const [mintLoading, setMintLoading] = useState(false);
  const [mintError, setMintError] = useState<string | null>(null);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentPayload, setPaymentPayload] = useState<SigningPayload | null>(null);

  // Current mint tracking
  const [currentMintIds, setCurrentMintIds] = useState<string[]>([]);
  const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);

  // Mint status tracking
  const [mintStatus, setMintStatus] = useState<MintStatus | null>(null);
  const [showMintStatus, setShowMintStatus] = useState(false);
  const mintStatusIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollMintStatusRef = useRef<(() => Promise<void>) | null>(null);

  // Pending mint state - track all pending mints in a batch
  const [pendingMint, setPendingMint] = useState<UserMint | null>(null);
  const [pendingMintIds, setPendingMintIds] = useState<string[]>([]);
  const [resumeLoading, setResumeLoading] = useState(false);

  // Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelCountdown, setCancelCountdown] = useState(10);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Check for pending mints - find all with same batch_id
  const checkPendingMints = useCallback(async () => {
    if (!launchSlug || !isAuthenticated) {
      setPendingMint(null);
      setPendingMintIds([]);
      return;
    }

    try {
      const result = await getMyMints(launchSlug, { limit: 50 }); // Get more to capture full batch
      const pendingMints = result.mints.filter(m => m.status === 'payment_pending');

      if (pendingMints.length > 0) {
        // Get the first pending mint (representative of the batch)
        const firstPending = pendingMints[0];
        // Find all mints with the same batch_id
        const batchMints = pendingMints.filter(m => m.batch_id === firstPending.batch_id);
        setPendingMint(firstPending);
        setPendingMintIds(batchMints.map(m => m.id));
      } else {
        setPendingMint(null);
        setPendingMintIds([]);
      }
    } catch (err) {
      console.error('Failed to check pending mints:', err);
      setPendingMint(null);
      setPendingMintIds([]);
    }
  }, [launchSlug, isAuthenticated]);

  // Check for pending mints on mount and when auth changes
  useEffect(() => {
    checkPendingMints();
  }, [checkPendingMints]);

  // Poll for mint status - store in ref to avoid effect dependency issues
  const pollMintStatus = useCallback(async () => {
    if (!launchSlug || currentMintIds.length === 0) return;

    try {
      const status = await getMintStatus(launchSlug, currentMintIds[0]);
      setMintStatus(status);

      // Check if we're done
      const finalStatuses = ['completed', 'failed', 'expired'];
      if (finalStatuses.includes(status.mint.status)) {
        // Clear the interval
        if (mintStatusIntervalRef.current) {
          clearInterval(mintStatusIntervalRef.current);
          mintStatusIntervalRef.current = null;
        }

        // Refresh data
        onRefresh?.();

        // Show notification
        if (status.mint.status === 'completed') {
          showSuccess('Mint successful! Check your notifications.');
          onMintSuccess?.();
        } else if (status.mint.status === 'failed') {
          showError(status.mint.error_message || 'Mint failed');
        }
      }
    } catch (err) {
      console.error('Failed to poll mint status:', err);
    }
  }, [launchSlug, currentMintIds, onRefresh, onMintSuccess, showSuccess, showError]);

  // Keep ref in sync with latest callback
  useEffect(() => {
    pollMintStatusRef.current = pollMintStatus;
  }, [pollMintStatus]);

  // Start polling when we have mint IDs
  useEffect(() => {
    if (currentMintIds.length > 0 && showMintStatus) {
      // Initial poll using ref
      pollMintStatusRef.current?.();

      // Poll every 3 seconds using ref wrapper
      mintStatusIntervalRef.current = setInterval(() => {
        pollMintStatusRef.current?.();
      }, 3000);
    }

    return () => {
      if (mintStatusIntervalRef.current) {
        clearInterval(mintStatusIntervalRef.current);
        mintStatusIntervalRef.current = null;
      }
    };
  }, [currentMintIds, showMintStatus]);  // No pollMintStatus dependency - use ref instead

  // Handle mint request
  const handleMint = async () => {
    if (!launchSlug || !isAuthenticated) return;

    try {
      setMintLoading(true);
      setMintError(null);

      const referralCode = launchId ? getMintReferralCode(launchId) : null;
      const result = await requestMint(launchSlug, quantity, referralCode, null, selectedEditionId);
      setCurrentMintIds(result.mintIds);
      setCurrentBatchId(result.batchId);

      // Free mint - skip payment modal
      if (result.status === 'payment_confirmed') {
        setShowMintStatus(true);
        showSuccess(result.message || 'Free mint started!');
        return;
      }

      // Show payment modal — normalize Xaman payload or client-signing intent.
      if (result.signingIntent || result.xaman) {
        setPaymentPayload(toSigningPayload(result.signingIntent ?? result.xaman));
        setShowPaymentModal(true);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start mint';
      setMintError(message);
      showError(message);
      // Supply may have moved (e.g. edition sold out during the request) —
      // refresh so the gallery/eligibility reflect current availability.
      onRefresh?.();
    } finally {
      setMintLoading(false);
    }
  };

  // Handle payment success
  const handlePaymentSuccess = (txid: string) => {
    setShowPaymentModal(false);
    setPaymentPayload(null);
    setShowMintStatus(true);
    setPendingMint(null);
    setPendingMintIds([]);
    const nftCount = currentMintIds.length;
    showSuccess(`Payment received! Minting your NFT${nftCount > 1 ? 's' : ''}...`);
  };

  // Handle payment rejected
  const handlePaymentRejected = () => {
    setShowPaymentModal(false);
    setPaymentPayload(null);
    setCurrentMintIds([]);
    setCurrentBatchId(null);
    showError('Payment was rejected');
  };

  // Handle payment expired
  const handlePaymentExpired = () => {
    setShowPaymentModal(false);
    setPaymentPayload(null);
    setCurrentMintIds([]);
    setCurrentBatchId(null);
    showError('Payment request expired');
  };

  // Handle resume pending mint
  const handleResume = async () => {
    if (!launchSlug || !pendingMint) return;

    try {
      setResumeLoading(true);
      setMintError(null);

      const result = await resumeMint(launchSlug, pendingMint.batch_id);
      // Track all mint IDs in the batch
      setCurrentMintIds(pendingMintIds.length > 0 ? pendingMintIds : [pendingMint.id]);
      setCurrentBatchId(pendingMint.batch_id);

      // Show payment modal with new payload — normalize Xaman or client-signing intent.
      if (result.signingIntent || result.xaman) {
        setPaymentPayload(toSigningPayload(result.signingIntent ?? result.xaman));
        setShowPaymentModal(true);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resume mint';
      // Check if reservation expired (various error message formats)
      const isExpired = message.toLowerCase().includes('expired') ||
        message.toLowerCase().includes('invalid status');
      if (isExpired) {
        setPendingMint(null);
        setPendingMintIds([]);
        // Use success notification since this is informational, not an error
        showSuccess('Your reservation has expired. You can start a new mint.');
      } else {
        setMintError(message);
        showError(message);
      }
    } finally {
      setResumeLoading(false);
    }
  };

  // Open cancel confirmation modal
  const openCancelModal = () => {
    setShowCancelModal(true);
    setCancelCountdown(10);

    // Start countdown
    const interval = setInterval(() => {
      setCancelCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Close cancel modal
  const closeCancelModal = () => {
    setShowCancelModal(false);
  };

  // Handle cancel mint
  const handleCancel = async () => {
    if (!launchSlug || !pendingMint) return;

    try {
      setCancelLoading(true);

      await cancelMint(launchSlug, pendingMint.batch_id);

      // Clear pending mint state
      setPendingMint(null);
      setPendingMintIds([]);
      setShowCancelModal(false);
      showSuccess('Reservation cancelled. You can now start a new mint.');

      // Refresh data
      onRefresh?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel mint';
      if (message === 'PAYMENT_DETECTED') {
        // Payment was found - show success and start polling
        setShowCancelModal(false);
        setCurrentMintIds(pendingMintIds.length > 0 ? pendingMintIds : [pendingMint.id]);
        setCurrentBatchId(pendingMint.batch_id);
        setShowMintStatus(true);
        const nftCount = pendingMintIds.length || 1;
        showSuccess(`Payment detected! Minting your NFT${nftCount > 1 ? 's' : ''}...`);
        setPendingMint(null);
        setPendingMintIds([]);
      } else {
        // Check if reservation expired (various error message formats)
        const isExpired = message.toLowerCase().includes('expired') ||
          message.toLowerCase().includes('invalid status');
        if (isExpired) {
          // Reservation already expired - clear state and show info message
          setShowCancelModal(false);
          setPendingMint(null);
          setPendingMintIds([]);
          showSuccess('Your reservation has expired. You can start a new mint.');
          onRefresh?.();
        } else {
          showError(message);
        }
      }
    } finally {
      setCancelLoading(false);
    }
  };

  // Close mint status modal
  const closeMintStatusModal = () => {
    setShowMintStatus(false);
    setMintStatus(null);
    setCurrentMintIds([]);
    setCurrentBatchId(null);
    onRefresh?.();
  };

  // Close payment modal
  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentPayload(null);
  };

  return {
    // Quantity state
    quantity,
    setQuantity,

    // Edition selection
    selectedEditionId,
    setSelectedEditionId,

    // Loading/error state
    mintLoading,
    mintError,

    // Payment modal state
    showPaymentModal,
    paymentPayload,

    // Mint status tracking
    mintStatus,
    showMintStatus,

    // Pending mint state
    pendingMint,
    pendingMintCount: pendingMintIds.length,
    resumeLoading,

    // Cancel modal state
    showCancelModal,
    cancelCountdown,
    cancelLoading,

    // Current mint tracking
    currentMintIds,
    currentBatchId,

    // Actions
    handleMint,
    handleResume,
    handleCancel,
    openCancelModal,
    closeCancelModal,
    handlePaymentSuccess,
    handlePaymentRejected,
    handlePaymentExpired,
    closeMintStatusModal,
    closePaymentModal,

    // Utilities
    checkPendingMints,
  };
}
